import { cx } from "@emotion/css";
import { useTranslation } from "react-i18next";
import { IconButton } from "src/components/Default/LekUI/Button";
import { Tooltip } from "src/components/Default/LekUI/Tooltip";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import { getUserId, getUserInfo, isStaff } from "src/hooks/useUser";
import DeleteIcon from "src/images/Icons/newIcons/exam/comments/delete.svg";
import LikeIcon from "src/images/Icons/newIcons/exam/comments/like.svg";
import ReplyIcon from "src/images/Icons/newIcons/exam/comments/reply.svg";

type Id = string | number;

interface CommentActionsComment {
	id: Id;
	user: { id: Id };
	likes: { userId: Id }[];
}

interface CommentActionsProps {
	comment: CommentActionsComment;
	className?: string;
	isReply?: boolean;
	setIsReply?: (isReply: boolean) => void;
	allowReply?: boolean;
	onRemoveComment: (params: { id: Id }) => Promise<void> | void;
	onToggleLike: (params: { id: Id }) => void;
}

export const CommentActions = ({
	comment,
	className,
	isReply,
	setIsReply,
	allowReply,
	onRemoveComment,
	onToggleLike,
}: CommentActionsProps) => {
	const { openModal } = useModals();
	const { t } = useTranslation();

	const userInfo = getUserInfo() as { id?: Id };
	const isLiked = comment.likes.some((like) => like.userId === getUserId());
	const canRemove = isStaff() || userInfo.id === comment.user.id;

	const openRemoveModal = () =>
		openModal(ModalsNames.DeleteItemModal, {
			title: t("modals.deleteItem.comment.title", "Usunąć komentarz?"),
			description: t(
				"modals.deleteItem.comment.description",
				"Komentarz zostanie trwale usunięty. Tej operacji nie można cofnąć.",
			),
			confirmText: t("modals.deleteItem.comment.confirm", "Usuń komentarz"),
			onDelete: async () => {
				await onRemoveComment({ id: comment.id });
			},
		});

	return (
		<div className={cx("flex items-center justify-end", className)}>
			{canRemove && (
				<Tooltip title={t("tooltips.removeComment")}>
					<IconButton
						edge="end"
						aria-label="remove comment"
						size="small"
						onClick={openRemoveModal}
					>
						<DeleteIcon className="icon-small icon-error" />
					</IconButton>
				</Tooltip>
			)}
			{allowReply && (
				<Tooltip title={t("tooltips.replyComment")}>
					<IconButton
						edge="end"
						aria-label="reply comment"
						size="small"
						onClick={() => setIsReply?.(!isReply)}
					>
						<ReplyIcon className="icon-small" />
					</IconButton>
				</Tooltip>
			)}
			<div className="flex-center-row">
				<Tooltip
					title={t(
						isLiked ? "tooltips.dislikeComment" : "tooltips.likeComment",
					)}
				>
					<IconButton
						edge="end"
						aria-label="like comment"
						size="small"
						onClick={() => onToggleLike({ id: comment.id })}
					>
						<LikeIcon className={cx("icon-small", isLiked && "icon-primary")} />
					</IconButton>
				</Tooltip>
				{comment.likes.length > 0 && (
					<span className={cx("size-14-light", isLiked && "text-primary")}>
						{comment.likes.length}
					</span>
				)}
			</div>
		</div>
	);
};
